import type { QueryClient } from '@tanstack/react-query';
import { projectElementKeys } from '@/hooks/api/useProjectElements';
import {
  elementDetailKeys,
} from '@/hooks/api/useProjectElementsDetail';
import {
  removeDeletedResourceLibraryItemFromCache,
  type ProjectElementType,
  type ResourceLibraryType,
} from '@/hooks/api/useResourceLibrary';

type ElementIdField = 'characterId' | 'locationId' | 'propId';

const ELEMENT_TYPE_TO_ID_FIELD: Record<ProjectElementType, ElementIdField> = {
  character: 'characterId',
  location: 'locationId',
  prop: 'propId',
};

const ELEMENT_TYPE_TO_LIST_SEGMENT: Record<ProjectElementType, string> = {
  character: 'characters',
  location: 'locations',
  prop: 'props',
};

function isProjectElementListQuery(queryKey: readonly unknown[], elementType: ProjectElementType): boolean {
  return (
    Array.isArray(queryKey) &&
    queryKey[0] === projectElementKeys.all[0] &&
    queryKey[1] === ELEMENT_TYPE_TO_LIST_SEGMENT[elementType]
  );
}

function removeDeletedProjectElementFromListCaches(
  queryClient: QueryClient,
  elementType: ProjectElementType,
  elementId: string
): void {
  const idField = ELEMENT_TYPE_TO_ID_FIELD[elementType];

  queryClient.setQueriesData<Record<ElementIdField, string>[]>(
    {
      predicate: (query) => isProjectElementListQuery(query.queryKey, elementType),
    },
    (current) => current?.filter((item) => item[idField] !== elementId)
  );
}

function removeDeletedProjectElementDetailCache(
  queryClient: QueryClient,
  projectId: string | null | undefined,
  elementType: ProjectElementType,
  elementId: string
): void {
  if (!projectId) {
    void queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== elementDetailKeys.all[0]) return false;
        return key.includes(elementId);
      },
    });
    return;
  }

  const detailKey =
    elementType === 'character'
      ? elementDetailKeys.character(projectId, elementId)
      : elementType === 'location'
        ? elementDetailKeys.location(projectId, elementId)
        : elementDetailKeys.prop(projectId, elementId);

  queryClient.removeQueries({ queryKey: detailKey });
}

export function removeDeletedProjectElementFromAllCaches(
  queryClient: QueryClient,
  params: {
    elementId: string;
    elementType: ProjectElementType;
    projectId?: string | null;
  }
): void {
  const { elementId, elementType, projectId } = params;

  removeDeletedResourceLibraryItemFromCache(queryClient, {
    resourceKind: elementType as ResourceLibraryType,
    resourceId: elementId,
  });
  removeDeletedProjectElementFromListCaches(queryClient, elementType, elementId);
  removeDeletedProjectElementDetailCache(queryClient, projectId, elementType, elementId);
}

export function toProjectElementType(elementType?: string | null): ProjectElementType | null {
  const normalized = elementType?.trim();
  if (normalized === 'character' || normalized === 'location' || normalized === 'prop') {
    return normalized;
  }
  return null;
}
